import * as React from 'react';
import { PersistentStorageManager } from '../../../../../models/Storage';

export type StaticColumnId =
	| 'name'
	| 'tags'
	| 'contactInfo'
	| 'creationDate'
	| 'jobTitle'
	| 'birthday'
	| 'anniversary'
	| 'age'
	| 'contactOwner'
	| 'lastCommunicationDate';

// Connection-type column ids are `connectionType:<type name>`.
export const CONNECTION_TYPE_COLUMN_PREFIX = 'connectionType:';

export type ConnectionTypeColumnId = `${typeof CONNECTION_TYPE_COLUMN_PREFIX}${string}`;

export type ColumnId = StaticColumnId | ConnectionTypeColumnId;

/** Logical grouping used to organize columns in the Table Settings panel. */
export type ColumnGroup = 'contactDetails' | 'connections';

export interface ColumnDefinition {
	id: ColumnId;
	label: string;
	isRequired: boolean;
	isVisible: boolean;
	group: ColumnGroup;
}

const DEFAULT_COLUMNS: ColumnDefinition[] = [
	{ id: 'name', label: 'Name', isRequired: true, isVisible: true, group: 'contactDetails' },
	{ id: 'tags', label: 'Tags', isRequired: false, isVisible: true, group: 'contactDetails' },
	{ id: 'contactInfo', label: 'Contact Info', isRequired: false, isVisible: true, group: 'contactDetails' },
	{ id: 'creationDate', label: 'Creation Date', isRequired: false, isVisible: true, group: 'contactDetails' },
	{ id: 'jobTitle', label: 'Job Title', isRequired: false, isVisible: false, group: 'contactDetails' },
	{ id: 'birthday', label: 'Birthday', isRequired: false, isVisible: false, group: 'contactDetails' },
	{ id: 'anniversary', label: 'Anniversary', isRequired: false, isVisible: false, group: 'contactDetails' },
	{ id: 'age', label: 'Age', isRequired: false, isVisible: false, group: 'contactDetails' },
	{
		id: 'lastCommunicationDate',
		label: 'Last Communication',
		isRequired: false,
		isVisible: false,
		group: 'contactDetails',
	},
	{ id: 'contactOwner', label: 'Contact Owner', isRequired: false, isVisible: false, group: 'connections' },
];

const TABLE_COLUMNS_STORAGE_KEY = 'contacts-table-column-visibility';
const MAX_VISIBLE_COLUMNS = 8;

/** Persisted visibility preferences keyed by column id. */
type VisibilityOverrides = Record<string, boolean>;

const loadVisibilityOverrides = (): VisibilityOverrides => {
	try {
		const stored = PersistentStorageManager.local.get(TABLE_COLUMNS_STORAGE_KEY);
		if (stored) {
			const parsed = JSON.parse(stored);
			if (!Array.isArray(parsed)) {
				return {};
			}
			const storedSettings = parsed as { id: string; isVisible: boolean }[];
			return storedSettings.reduce<VisibilityOverrides>((overrides, setting) => {
				overrides[setting.id] = setting.isVisible;
				return overrides;
			}, {});
		}
	} catch (error) {
		console.warn('Failed to parse stored column visibility preferences:', error);
	}
	return {};
};

const persistVisibility = (columns: ColumnDefinition[]) => {
	try {
		// Legacy `{ id, isVisible }[]` shape, kept for backward compat.
		const visibilitySettings = columns.map(({ id, isVisible }) => ({ id, isVisible }));
		PersistentStorageManager.local.setObject(TABLE_COLUMNS_STORAGE_KEY, visibilitySettings);
	} catch (error) {
		console.warn('Failed to save column visibility preferences:', error);
	}
};

// `dynamicColumns` are merged with DEFAULT_COLUMNS; caller must memoize them.
export const useTableColumnVisibility = (dynamicColumns: ColumnDefinition[] = []) => {
	const [overrides, setOverrides] = React.useState<VisibilityOverrides>(loadVisibilityOverrides);

	const baseColumns = React.useMemo<ColumnDefinition[]>(
		() => [...DEFAULT_COLUMNS, ...dynamicColumns],
		[dynamicColumns]
	);

	// Required columns are always visible; otherwise a stored preference wins over the default.
	const resolveVisibility = React.useCallback(
		(column: ColumnDefinition, visibilityOverrides: VisibilityOverrides) =>
			column.isRequired ? true : (visibilityOverrides[column.id] ?? column.isVisible),
		[]
	);

	const columns = React.useMemo<ColumnDefinition[]>(() => {
		return baseColumns.map(column => {
			const isVisible = resolveVisibility(column, overrides);
			return isVisible === column.isVisible ? column : { ...column, isVisible };
		});
	}, [baseColumns, overrides, resolveVisibility]);

	const visibleColumns = React.useMemo(() => columns.filter(col => col.isVisible), [columns]);
	const visibleColumnCount = visibleColumns.length;
	const canAddMoreColumns = visibleColumnCount < MAX_VISIBLE_COLUMNS;

	const updateColumnVisibility = React.useCallback(
		(columnId: ColumnId, isVisible: boolean) => {
			// Compute inside the updater so batched toggles see the running count, not a stale closure.
			setOverrides(prev => {
				const target = baseColumns.find(col => col.id === columnId);
				// Don't allow hiding required columns.
				if (!target || target.isRequired) {
					return prev;
				}
				const isCurrentlyVisible = resolveVisibility(target, prev);
				const currentVisibleCount = baseColumns.filter(col => resolveVisibility(col, prev)).length;
				// Don't allow exceeding the visible-column limit.
				if (isVisible && !isCurrentlyVisible && currentVisibleCount >= MAX_VISIBLE_COLUMNS) {
					return prev;
				}

				const next = { ...prev, [columnId]: isVisible };
				persistVisibility(baseColumns.map(col => ({ ...col, isVisible: resolveVisibility(col, next) })));
				return next;
			});
		},
		[baseColumns, resolveVisibility]
	);

	const resetToDefaults = React.useCallback(() => {
		setOverrides({});
		PersistentStorageManager.local.remove(TABLE_COLUMNS_STORAGE_KEY);
	}, []);

	return {
		columns,
		visibleColumns,
		visibleColumnCount,
		maxVisibleColumns: MAX_VISIBLE_COLUMNS,
		canAddMoreColumns,
		updateColumnVisibility,
		resetToDefaults,
	};
};
